
export class SisIdValidator {

	constructor(public readonly minLength: number = 1) {}

	public notEmpty(username: string): boolean {
		return username.length > 0;
	}

	public meetsMinLength(sisId: string): boolean {
		return sisId.length >= this.minLength;
	}

	public hasNoLeadingOrTrailingWhitespace(sisId: string): boolean {
		return sisId.trim() === sisId;
	}

	public hasNoInnerWhitespace(sisId: string): boolean {
		return sisId.replace(/\s+/, " ") === sisId;
	}

	public validateAll(sisId: string) {
		return {
			notEmpty:                         this.notEmpty(sisId),
			meetsMinLength:                   this.meetsMinLength(sisId),
			hasNoInnerWhitespace:             this.hasNoInnerWhitespace(sisId),
			hasNoLeadingOrTrailingWhitespace: this.hasNoLeadingOrTrailingWhitespace(sisId)
		};
	}
}

function getMbCharAt(input: string, pos: number): string {
	const slice = input.slice(pos);
	const codePoint = input.codePointAt(pos) ?? 0;
	if (0xfe00 <= codePoint && codePoint <= 0xfe0f) {
		return input.slice(pos - 1, pos + 2);
	}
	const multiEmojiMatch = slice.match(/[\p{Emoji}\u200d]+/gu);
	if (multiEmojiMatch) {
		return multiEmojiMatch[0];
	}
	const singleEmojiMatch = slice.match(/\p{Emoji}/gu);
	if (singleEmojiMatch) {
		return singleEmojiMatch[0];
	}
	let zalgo = "";
	for (let i = pos; i < input.length; i++) {
		const point = input.codePointAt(i);
		if (!(point && 0x300 <= point && point <= 0x36f)) {
			break;
		}
		zalgo += input[i];
	}
	if (zalgo) {
		return input[pos - 1] + zalgo;
	}
	return codePoint === 0 ? "" : String.fromCodePoint(codePoint);
}

function characterRegExpDefToRegExp(def: string): RegExp {
	const delim = def.charAt(0);
	const parts = def.substr(1).split(delim);
	const args = parts.pop();

	return new RegExp(parts.join(delim), args);
}

export class UsernameValidator {

	private invalidCharacterRegExp: RegExp;

	constructor(
		public readonly minLength: number,
		public readonly maxLength: number,
		invalidCharRegexDef: string
	) {
		this.invalidCharacterRegExp = characterRegExpDefToRegExp(invalidCharRegexDef);
	}

	public notEmpty(username: string): boolean {
		return username.length > 0;
	}

	public meetsMinLength(username: string): boolean {
		return username.length >= this.minLength;
	}

	public meetsMaxLength(username: string): boolean {
		return username.length <= this.maxLength;
	}

	public hasNoLeadingOrTrailingWhitespace(username: string): boolean {
		return username.trim() === username;
	}

	public hasNoInnerWhitespace(username: string): boolean {
		return username.replace(/\s+/, " ") === username;
	}

	public findInvalidCharacter(username: string): string|null {
		const match = username.match(this.invalidCharacterRegExp);
		return match ? getMbCharAt(username, username.indexOf(match[0])) : null;
	}

	public charactersAreSane(username: string): boolean {
		return this.findInvalidCharacter(username) === null;
	}

	public validateAll(username: string) {
		return {
			notEmpty:                         this.notEmpty(username),
			meetsMinLength:                   this.meetsMinLength(username),
			meetsMaxLength:                   this.meetsMaxLength(username),
			hasNoLeadingOrTrailingWhitespace: this.hasNoLeadingOrTrailingWhitespace(username),
			hasNoInnerWhitespace:             this.hasNoInnerWhitespace(username),
			characterSanity:                  this.charactersAreSane(username)
		};
	}
}

export class PasswordValidator{

	private readonly invalidCharRegex: RegExp;
	private readonly specialCharRegex: RegExp;

	constructor(
		public readonly minStudentPasswordLength: number,
		public readonly minPersonnelPasswordLength: number,
		public readonly validSpecialChars: string
	) {
		const escaped = validSpecialChars.replace(/]/, "\\]");
		this.invalidCharRegex = new RegExp(`([^a-zA-Z0-9 ${escaped}])`);
		this.specialCharRegex = new RegExp(`([${escaped}])`);
	}

	public hasNoLeadingOrTrailingSpaces(password: string): boolean {
		return password.match(/(^ | $)/) === null;
	}

	public findInvalidCharacter(password: string): string|null {
		const matches = password.match(this.invalidCharRegex);
		return matches ? getMbCharAt(password, password.indexOf(matches[1])) : null;
	}

	public allPasswordCharactersAreValid(password: string): boolean {
		return this.findInvalidCharacter(password) === null;
	}

	public meetsStudentMinLength(password: string): boolean {
		return password.length >= this.minStudentPasswordLength;
	}

	public containsCharacters(password: string): boolean {
		return password.match(/^[ ]+$/) === null;
	}

	public meetsPersonnelMinLength(password: string): boolean {
		return password.length >= this.minPersonnelPasswordLength;
	}

	public passwordIncludesNumber(password: string): boolean {
		return password.match(/[0-9]/) !== null;
	}

	public passwordIncludesLowerCaseCharacter(password: string): boolean {
		return password.match(/[a-z]/) !== null;
	}

	public passwordIncludesUpperCaseCharacter(password: string): boolean {
		return password.match(/[A-Z]/) !== null;
	}

	public passwordIncludesSpecialCharacter(password: string): boolean {
		return password.match(this.specialCharRegex) !== null;
	}

	public validateAllStudent(password: string) {
		return {
			meetsStudentMinLength:        this.meetsStudentMinLength(password),
			containsCharacters:           this.containsCharacters(password),
			allCharactersAreValid:        this.allPasswordCharactersAreValid(password),
		};
	}

	public validateAllPersonnel(password: string) {
		return {
			meetsPersonnelMinLength:      this.meetsPersonnelMinLength(password),
			includesLowerCaseCharacter:   this.passwordIncludesLowerCaseCharacter(password),
			includesUpperCaseCharacter:   this.passwordIncludesUpperCaseCharacter(password),
			includesSpecialCharacter:     this.passwordIncludesSpecialCharacter(password),
			includesNumber:               this.passwordIncludesNumber(password),
			hasNoLeadingOrTrailingSpaces: this.hasNoLeadingOrTrailingSpaces(password),
			allCharactersAreValid:        this.allPasswordCharactersAreValid(password)
		};
	}

}

export class NameValidator {
	public notEmpty(name: string): boolean {
		return name.length >= 1;
	}

	public hasNoLeadingOrTrailingWhitespace(name: string): boolean {
		return name.trim() === name;
	}

	public validateAll(name: string) {
		return {
			notEmpty:                         this.notEmpty(name),
			hasNoLeadingOrTrailingWhitespace: this.hasNoLeadingOrTrailingWhitespace(name)
		};
	}
}

